import { jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import { getDataTestId } from "../../Util/index.js";
import { INVALID_PROPS_ERROR, SUPPORTED_VARIANTS, COMPONENT_NAME } from "./constants.js";
import { filterValidChildren } from "./lib.js";
import { ExpandableList } from "./view.expandable.js";
import { SimpleList, InlineList, DividedInlineList } from "./styles.js";
const List = forwardRef((props, ref) => {
  const {
    variant = "UnorderedList",
    children,
    uniqueId,
    ...restOfProps
  } = props;
  const validChildren = filterValidChildren(children);
  if (!validChildren.length) {
    console.error(`${INVALID_PROPS_ERROR} Missing required children prop.`);
    return null;
  }
  if (!SUPPORTED_VARIANTS.includes(variant)) {
    console.error(
      `${INVALID_PROPS_ERROR} Invalid prop 'variant' of value '${variant}' supplied to 'List', expected one of ['SimpleList', 'OrderedList', 'UnorderedList', 'InlineList', 'DividedInlineList'].`
    );
    return null;
  }
  if (variant === "ExpandableList") {
    const childrenArray = Array.isArray(children) ? children : [children].filter(Boolean);
    const {
      ariaLabeledById,
      buttonId,
      showMoreText,
      defaultItemsToExpose
    } = restOfProps;
    if (!ariaLabeledById || !buttonId || !showMoreText) {
      console.error(
        `${INVALID_PROPS_ERROR} Missing required props for ExpandableList variant. Required: ariaLabeledById, buttonId, showMoreText`
      );
      return null;
    }
    return /* @__PURE__ */ jsx(
      ExpandableList,
      {
        ariaLabeledById,
        buttonId,
        showMoreText,
        defaultItemsToExpose,
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        children: childrenArray
      }
    );
  }
  if (variant === "OrderedList") {
    return /* @__PURE__ */ jsx(
      "ol",
      {
        role: validChildren.length > 0 ? "list" : void 0,
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        ...restOfProps,
        ref,
        children
      }
    );
  }
  if (variant === "UnorderedList") {
    return /* @__PURE__ */ jsx(
      "ul",
      {
        role: validChildren.length > 0 ? "list" : void 0,
        "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
        ...restOfProps,
        ref,
        children
      }
    );
  }
  const commonProps = {
    role: validChildren.length > 0 ? "list" : void 0,
    "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
    ...restOfProps,
    ref
  };
  if (variant === "SimpleList") {
    return /* @__PURE__ */ jsx(SimpleList, { ...commonProps, children });
  }
  if (variant === "InlineList") {
    return /* @__PURE__ */ jsx(InlineList, { ...commonProps, children });
  }
  return /* @__PURE__ */ jsx(DividedInlineList, { ...commonProps, children });
});
List.displayName = COMPONENT_NAME;
export {
  List
};
